import {
	DownloadFilteredRequestParams,
	GetUnassignedListRequestPaginationParams,
} from "@/pages/Private/pages/List/schema/list";

/**
 * Serializes the shared prospect-search query params (filters as JSON, sort flattened) used by the
 * `list/all-prospects`, `list/unassigned`, `list/unassigned-and-dummy` and `list/count-all-prospects`
 * endpoints. Keeps the historical key order so later filter keys still override anything passed
 * through `...params`. Endpoint-specific params (e.g. `listPersonIds`, `specialType` on
 * all-prospects) are destructured out by the caller and merged afterwards.
 */
export const buildListSearchQueryParams = ({
	sorting,
	filter,
	assignStatus,
	inclusionFilterStatus,
	exclusionReasons,
	priorityFilterStatus,
	signalFilterStatus,
	sentimentFilterStatus,
	outboxTabs,
	campaignIds,
	...params
}: Partial<GetUnassignedListRequestPaginationParams>): Record<string, unknown> => ({
	...(assignStatus && { assignStatus: JSON.stringify(assignStatus) }),
	// Comma-joined to match the API DTO's split transform (PREC-3850).
	...(outboxTabs && outboxTabs.length > 0 && { outboxTabs: outboxTabs.join(",") }),
	...(campaignIds && campaignIds.length > 0 && { campaignIds: campaignIds.join(",") }),
	...params,
	...(sorting && { ...sorting }),
	...(filter && { filter: JSON.stringify(filter) }),
	inclusionFilterStatus: JSON.stringify(inclusionFilterStatus),
	...(exclusionReasons &&
		exclusionReasons.length > 0 && { exclusionReasons: JSON.stringify(exclusionReasons) }),
	priorityFilterStatus: JSON.stringify(priorityFilterStatus),
	...(signalFilterStatus && { signalFilterStatus: JSON.stringify(signalFilterStatus) }),
	...(sentimentFilterStatus &&
		sentimentFilterStatus.length > 0 && {
			sentimentFilterStatus: JSON.stringify(sentimentFilterStatus),
		}),
});

/**
 * Serializes the `list/download-filtered` query params (extracted from `list.api.ts`). Same
 * JSON conventions as above, plus the export-only `listPersonIds`/`selectType` params.
 */
export const buildDownloadFilteredQueryParams = ({
	sorting,
	filter,
	assignStatus,
	inclusionFilterStatus,
	exclusionReasons,
	priorityFilterStatus,
	signalFilterStatus,
	listPersonIds,
	selectType,
	...params
}: DownloadFilteredRequestParams): Record<string, unknown> => ({
	...(assignStatus && { assignStatus: JSON.stringify(assignStatus) }),
	...(params && { ...params }),
	...(sorting && { ...sorting }),
	...(filter && { filter: JSON.stringify(filter) }),
	inclusionFilterStatus: JSON.stringify(inclusionFilterStatus),
	...(exclusionReasons &&
		exclusionReasons.length > 0 && { exclusionReasons: JSON.stringify(exclusionReasons) }),
	priorityFilterStatus: JSON.stringify(priorityFilterStatus),
	...(signalFilterStatus && { signalFilterStatus: JSON.stringify(signalFilterStatus) }),
	...(listPersonIds !== undefined && { listPersonIds: JSON.stringify(listPersonIds) }),
	...(selectType && { selectType }),
});
